import { catchError, map, Observable, of, tap } from 'rxjs';

import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { FullMemberSimpleFragment } from '../../__generated__/generated';
import { MemberApiService } from './member-api.service';
import { SecureStorageService } from './secure-storage.service';
import { ToastService } from './toast.service';
import { UtilsService } from './utils.service';

const KEY_TOKEN = 'AccessToken';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  protected readonly utils = inject(UtilsService);

  private readonly secureStorage = inject(SecureStorageService);
  private readonly memberApi = inject(MemberApiService);
  private readonly transloco = inject(TranslocoService);
  private readonly toast = inject(ToastService);

  private readonly _accessToken = signal<string | null>(null);
  private readonly _member = signal<FullMemberSimpleFragment | null>(null);
  private readonly _isAuthServiceReady = signal(false);

  readonly member = this._member.asReadonly();
  readonly isAuthServiceReady = this._isAuthServiceReady.asReadonly();
  readonly isAuthenticated = computed(() => this._accessToken() !== null);

  async saveAccessToken(accessToken: string): Promise<void> {
    await this.secureStorage.setItem(KEY_TOKEN, accessToken);
    this._accessToken.set(accessToken);
  }

  setMember(member: FullMemberSimpleFragment): void {
    this._member.set(member);
  }

  async cleanUpLoginData(): Promise<void> {
    await this.secureStorage.removeItem(KEY_TOKEN);
    this._accessToken.set(null);
    this._member.set(null);
  }

  /**
   * Rehydrate session from secure storage on app start.
   * Call this inside `APP_INITIALIZER` or `AppComponent.ngOnInit`.
   * Member data must be fetched via API separately and set with `setMember()`.
   */
  async restore(): Promise<void> {
    await this.secureStorage.init();
    const token = await this.secureStorage.getItem(KEY_TOKEN);
    if (token) {
      this._accessToken.set(token);
    }
    this._isAuthServiceReady.set(true);
  }

  /** Returns an Observable of the current member — consumed by the auth guard. */
  loadMemberData(): Observable<FullMemberSimpleFragment | null> {
    if (!this._accessToken()) {
      console.log('loadMemberData.case1');
      return of(null);
    }
    if (this._member()) {
      console.log('loadMemberData.case2');
      return of(this._member());
    }

    // return of(this._member());
    return this.memberApi.meGetMemberSimple().pipe(
      map((member) => {
        if (!member) {
          throw new Error('Failed to load member data');
        }
        return member;
      }),
      tap((member) => {
        console.log('loadMemberData.case3');
        this.setMember(member!);
      }),
      catchError((error) => {
        console.log('loadMemberData:', error);
        return of(null);
      }),
    );
  }

  /** Raw token — used by the HTTP interceptor to attach Authorization headers. */
  getAccessToken(): Promise<string | null> {
    return this.secureStorage.getItem(KEY_TOKEN);
  }

  logout(): void {
    this.memberApi.logout().subscribe(() => {
      this.cleanUpLoginData();
      this.toast.showToast('success', this.transloco.translate('msgTT.logoutSuccess'));
      this.utils.routeToHome();
    });
  }
}
